import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, get, type Programme } from '../api.js';
import { PageTitle } from '../frame.js';
import { StoryStatusBadge } from '../status-badge.js';
import { t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { Link, navigate } from '../router.js';
import { fmtDateTime, hhmmss, mmss, todayIso } from '../util.js';

interface DayStory {
  id: string;
  title: string;
  status: string;
  displayNumber: string | null;
  createdBy: string | null;
  programmeName: string;
  rundownId: string | null;
  readS: number;
  videoS: number;
  totalS: number;
  updatedAt: string;
}
interface DayResult {
  page: number;
  size: number;
  total: number;
  totalS: number;
  dates: [string, string];
  stories: DayStory[];
}

function Pager({ page, size, total, onPage }: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="pager">
      <a onClick={() => page > 1 && onPage(page - 1)}>{t('previous')}</a>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <a key={p} onClick={() => onPage(p)} style={p === page ? { fontWeight: 'bold', textDecoration: 'none' } : undefined}>
          {(p - 1) * size + 1} - {Math.min(p * size, total)} {t('of')} {total}
        </a>
      ))}
      <a onClick={() => page < pages && onPage(page + 1)}>{t('next')}</a>
    </div>
  );
}

export function DayListPage() {
  const [title, setTitle] = useState('');
  const [sort, setSort] = useState<'updatedAt' | 'title' | 'displayNumber' | 'programme' | 'totalS'>('updatedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DayResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (e?: FormEvent) => {
    e?.preventDefault();
    const q = new URLSearchParams({ page: String(page), sort, order, ...(title ? { title } : {}) });
    get<DayResult>(`/api/stories/day?${q}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sort, order]);

  const sortBy = (s: typeof sort) => {
    if (s === sort) setOrder(order === 'asc' ? 'desc' : 'asc');
    else {
      setSort(s);
      setOrder(s === 'updatedAt' ? 'desc' : 'asc');
    }
  };
  const arrow = (s: typeof sort) => (sort === s ? (order === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <>
      <PageTitle title={`${t('dayTitle')} (${t('department')}: TV)`} hint={t('dayHint')} />
      <form className="panel" onSubmit={load}>
        <div className="panel-title">{t('search')}</div>
        <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr max-content' }}>
          <label>{t('title')} :</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
          <button className="btn">➜ {t('go')}</button>
        </div>
      </form>
      {error && <div className="banner err">{error}</div>}
      {data && (
        <>
          <div className="toolbar">
            <button className="btn" onClick={() => window.print()}>
              🖨 {t('print')}
            </button>
          </div>
          <Pager page={data.page} size={data.size} total={data.total} onPage={setPage} />
          <div className="table-scroll">
            <table className="grid day-list">
              <thead>
                <tr>
                  <th>{t('colEdit')}</th>
                  <th onClick={() => sortBy('displayNumber')} style={{ cursor: 'pointer' }}>
                    {t('colNum')}
                    {arrow('displayNumber')}
                  </th>
                  <th onClick={() => sortBy('title')} style={{ cursor: 'pointer' }}>
                    {t('colTitle')}
                    {arrow('title')}
                  </th>
                  <th>{t('colCreatedBy')}</th>
                  <th onClick={() => sortBy('programme')} style={{ cursor: 'pointer' }}>
                    {t('colBulletin')}
                    {arrow('programme')}
                  </th>
                  <th>{t('colCat')}</th>
                  <th>{t('colRead')}</th>
                  <th>Χρ. VID</th>
                  <th onClick={() => sortBy('totalS')} style={{ cursor: 'pointer' }}>
                    Χρ. Συν.{arrow('totalS')}
                  </th>
                  <th onClick={() => sortBy('updatedAt')} style={{ cursor: 'pointer' }}>
                    {t('colUpdated')}
                    {arrow('updatedAt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.stories.map((s) => (
                  <tr key={s.id}>
                    <td className="actions">
                      <button className="icon-edit" title={t('editRecord')} onClick={() => navigate(`/story/${s.id}/edit`)} />
                    </td>
                    <td className="num">{s.displayNumber ?? ''}</td>
                    <td>
                      <Link to={`/story/${s.id}`}>
                        <b>{s.title}</b>
                      </Link>
                    </td>
                    <td>{s.createdBy ?? ''}</td>
                    <td className="c">{s.rundownId ? <Link to={`/rundown/${s.rundownId}`}>{s.programmeName}</Link> : s.programmeName}</td>
                    <td className="c cat">{s.status ? <StoryStatusBadge status={s.status} /> : '-'}</td>
                    <td className="num">{mmss(s.readS)}</td>
                    <td className="num">{mmss(s.videoS)}</td>
                    <td className="num">{hhmmss(s.totalS)}</td>
                    <td className="num">{fmtDateTime(s.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} size={data.size} total={data.total} onPage={setPage} />
          <p className="tip">
            {t('totalTime')} {hhmmss(data.totalS)}
          </p>
          <p className="tip">{t('tipDayDates')}</p>
        </>
      )}
    </>
  );
}

// ── Quick find / archive ───────────────────────────────────────────────────────
interface SearchStory {
  id: string;
  title: string;
  displayNumber: string | null;
  readComputedS: number;
  readOverrideS: number | null;
  videoS: number;
  updatedAt: string;
  read: boolean;
  placements: Array<{ date: string; programmeName: string; order: number }>;
}
interface SearchResult {
  page: number;
  size: number;
  total: number;
  stories: SearchStory[];
}

function SearchForm({ advanced }: { advanced: boolean }) {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: string; displayName: string }>>([]);
  const [f, setF] = useState<Record<string, string>>({ date: advanced ? '' : todayIso(), size: '50', sort: 'updatedAt', order: 'desc' });
  const [data, setData] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    get<{ programmes: Programme[] }>('/api/programmes').then((r) => setProgrammes(r.programmes));
    get<{ categories: Array<{ id: string; name: string }> }>('/api/categories').then((r) => setCategories(r.categories));
    get<{ users: Array<{ id: string; displayName: string }> }>('/api/users').then((r) => setUsers(r.users));
  }, []);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const search = (e?: FormEvent, p = 1) => {
    e?.preventDefault();
    if (!advanced && !f.date) return setError(t('tipRequired'));
    setPage(p);
    const q = new URLSearchParams({ page: String(p) });
    for (const [k, v] of Object.entries(f)) if (v) q.set(k, v);
    get<SearchResult>(`/api/stories/search?${q}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };
  const userOptions = users.map((u) => (
    <option key={u.id} value={u.id}>
      {u.displayName}
    </option>
  ));
  const buttons = (
    <div className="toolbar">
      <button type="button" className="btn" onClick={() => setF({ date: advanced ? '' : todayIso(), size: '50', sort: 'updatedAt', order: 'desc' })}>
        ↺ {t('clear')}
      </button>
      <button className="btn primary">🔎 {t('search')}</button>
    </div>
  );
  return (
    <>
      <form className="panel" onSubmit={search}>
        {buttons}
        <div className="form-grid">
          <label className={advanced ? '' : 'req'}>{t('bulletinDate')} :</label>
          <DateInput value={f.date ?? ''} onChange={(v) => setF({ ...f, date: v })} />
          {advanced ? (
            <>
              <label>{t('fromDate')} / {t('toDate')} :</label>
              <span>
                <DateInput value={f.from ?? ''} onChange={(v) => setF({ ...f, from: v })} /> – <DateInput value={f.to ?? ''} onChange={(v) => setF({ ...f, to: v })} />
              </span>
            </>
          ) : (
            <>
              <label>{t('origin')} :</label>
              <select value={f.originUserId ?? ''} onChange={set('originUserId')}>
                <option value="">—</option>
                {userOptions}
              </select>
            </>
          )}
          <label>{t('bulletin')} :</label>
          <select value={f.programmeId ?? ''} onChange={set('programmeId')}>
            <option value="">{t('choose')}</option>
            {programmes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <label>{t('category')} :</label>
          <select value={f.categoryId ?? ''} onChange={set('categoryId')}>
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {advanced && (
            <>
              <label>{t('origin')} :</label>
              <select value={f.originUserId ?? ''} onChange={set('originUserId')}>
                <option value="">—</option>
                {userOptions}
              </select>
              <label>{t('title')} :</label>
              <input value={f.title ?? ''} onChange={set('title')} />
              <label>{t('text')} :</label>
              <input value={f.q ?? ''} onChange={set('q')} className="wide" />
              <label>{t('comments')} :</label>
              <input value={f.comments ?? ''} onChange={set('comments')} />
              <label>{t('updatedFrom')} :</label>
              <select value={f.updatedById ?? ''} onChange={set('updatedById')}>
                <option value="">—</option>
                {userOptions}
              </select>
            </>
          )}
          <label>{t('createdFrom')} :</label>
          <select value={f.createdById ?? ''} onChange={set('createdById')}>
            <option value="">—</option>
            {userOptions}
          </select>
          <label>{t('records')} :</label>
          <select value={f.size} onChange={set('size')}>
            {['50', '100', '150'].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
          <label>{t('sorting')} :</label>
          <select value={f.sort} onChange={set('sort')}>
            <option value="updatedAt">{t('byUpdated')}</option>
            <option value="order">{t('byOrder')}</option>
          </select>
          <label>{t('direction')} :</label>
          <select value={f.order} onChange={set('order')}>
            <option value="desc">{t('desc')}</option>
            <option value="asc">{t('asc')}</option>
          </select>
        </div>
        {buttons}
      </form>
      <p className="tip">{t('tipOperators')}</p>
      {error && <div className="banner err">{error}</div>}
      {data && (
        <>
          <div className="toolbar">
            <button className="btn" onClick={() => window.print()}>
              🖨 {t('print')}
            </button>
          </div>
          <Pager page={page} size={data.size} total={data.total} onPage={(p) => search(undefined, p)} />
          <div className="table-scroll">
            <table className="grid find-list">
              <thead>
                <tr>
                  <th>{t('colEdit')}</th>
                  <th>{t('colTitle')}</th>
                  <th>{t('department')}</th>
                  <th>{t('byOrder')}</th>
                  <th>{t('bulletin')}</th>
                  <th>{t('bulletinDate')}</th>
                  <th>{t('colNum')}</th>
                  <th>{t('colRead')}</th>
                  <th>{t('colUpdated')}</th>
                </tr>
              </thead>
              <tbody>
                {data.stories.length === 0 && (
                  <tr>
                    <td colSpan={9} className="c muted">
                      {t('noResults')}
                    </td>
                  </tr>
                )}
                {data.stories.map((s) => (
                  <tr key={s.id}>
                    <td className="actions">
                      <button className="icon-edit" title={t('editRecord')} onClick={() => navigate(`/story/${s.id}/edit`)} />
                    </td>
                    <td>
                      <Link to={`/story/${s.id}`} style={{ color: s.read ? '#7a1f8a' : undefined }}>
                        <b>{s.title}</b>
                      </Link>
                    </td>
                    <td className="c">TV</td>
                    <td className="num">{s.placements.map((p) => p.order).join(', ')}</td>
                    <td className="c">{s.placements.map((p) => p.programmeName).join(', ') || '*****'}</td>
                    <td className="c">{s.placements.map((p) => p.date).join(', ')}</td>
                    <td className="num">{s.displayNumber ?? ''}</td>
                    <td className="num">{mmss(s.readOverrideS ?? s.readComputedS)}</td>
                    <td className="num">{fmtDateTime(s.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

export function QuickFindPage() {
  return (
    <>
      <PageTitle title={`${t('quickTitle')}: (TV)`} hint={t('searchHint')} />
      <SearchForm advanced={false} />
    </>
  );
}

export function ArchivePage() {
  return (
    <>
      <PageTitle title={t('archiveTitle')} hint={t('searchHint')} />
      <SearchForm advanced />
    </>
  );
}
